<template>
    <div class="app" @click="popupHandler">
        <div v-if="isImpersonated" class="exit-impersonation" @click="exitImpersonation">
            <img src="~@/assets/images/icons/left-arrow.svg" />
            Вернуться в мой профиль
        </div>
        <div class="app-loader-wrapper" v-if="isLoaderShown">
            <Loader />
            <div class="app-loader-message">Загрузка...</div>
        </div>
        <div
            v-else
            :class="{
                blurred: isPopupActive,
                wrap: isHeader,
                isMobileApp: isMobileApp,
                [`route-${$route.name}`]: true,
            }"
        >
            <div ref="refHeader">
                <Header v-if="isHeader && !isMobileApp" :class="{ header__impersonation: isImpersonated }" />
            </div>
            <div
                class="app-body"
                :class="{
                    'app-body_impersonation': isImpersonated,
                }"
                :style="{ 'min-height': bodyHeight }"
            >
                <router-view />
            </div>
            <div ref="refFooter">
                <Footer v-if="!isMobileApp" :style="{ margin: !$route.params.footerOffset ? 0 : 'inherit' }" />
            </div>
        </div>

        <div class="app-loader-wrapper app-loader--overlay" v-if="isAppLoaderEnabled">
            <Loader />
            <div class="app-loader-message">Загрузка...</div>
        </div>

        <Popup />
        <SnackBars />
        <SvgSprite />
        <ProfilePopup v-if="requiredToFill" :profile="userProfileInfo" v-model="showProfilePopup" />
        <portal-target name="modal" />
        <NotificationsApp v-if="!isLoading" />
        <SelectInterestsDialog v-if="!isLoading" />
    </div>
</template>

<script>
import { makeURL, registerSecurityService as httpRegisterSecurityService } from './tools/http';
import { registerSecurityService as wsConnectorRegisterSecurityService } from './tools/ws_connector';
import { wsSetServices } from './tools/ws_connector';

import Header from './views/Header';
import Popup from './components/Popup';
import SnackBars from './components/SnackBars';
import SvgSprite from './components/UI/SvgSprite';
import Loader from './components/UI/Loader';
import Footer from './views/Footer';

import { mapGetters, mapActions } from 'vuex';
import { init, setService } from '@/tools/websocket';
import ProfilePopup from '@/components/profile-popup/ProfilePopup.vue';
import NotificationsApp from '@/components/UI/notifications/NotificationsApp.vue';
import SelectInterestsDialog from '@/views/track/components/dialogs/SelectInterestsDialog.vue';

const getters = [
    'isAuthorized',
    'isPopupActive',
    'mobile_width',
    'bodyLocked',
    'htmlNoScroll',
    'contentHeight',
    'isMobileApp',
    'userHasAccess',
    'isAppLoaderEnabled',
    'userProfileInfo',
];

const actions = [
    'resize',
    'loadProfileData',
    // 'loadAuthData',

    // chat ws callBacks
    'room_created',
    'user_added_to_group',
    'room_users',
    'room_messages',
    'room_list',
    'new_message',
    'room_existed',
    'message_has_read',
    'loadDictionary',
    'loadNotifications',
    'onNotificationMessage',
];

const popupsControllerActions = ['closeAllPopups'];

export default {
    name: 'App',

    components: {
        ProfilePopup,
        Header,
        Popup,
        SnackBars,
        SvgSprite,
        Footer,
        Loader,
        NotificationsApp,
        SelectInterestsDialog,
    },

    data: () => ({
        width: window.innerWidth,
        isHeader: true,
        noneHeaderRoutes: ['projectoryTest'],
        showProfilePopup: true,
        isLoading: true,
    }),
    computed: {
        ...mapGetters(getters),
        ...mapGetters('auth', ['userId', 'isImpersonated']),

        requiredToFill() {
            return this.userProfileInfo.id && !this.userProfileInfo.important_fields_filled;
        },
        bodyHeight() {
            return `${this.contentHeight}px`;
        },

        isLoaderShown() {
            return !(this.userHasAccess || this.$route.meta.isOidcRoute);
        },
    },
    watch: {
        bodyLocked(v) {
            let body = document.querySelector('body');
            if (v) {
                body.classList.add('modal-open');
            } else {
                body.classList.remove('modal-open');
            }
        },
        htmlNoScroll(v) {
            let body = document.querySelector('html');
            if (v) {
                body.classList.add('no-scroll');
            } else {
                body.classList.remove('no-scroll');
            }
        },
        isAuthorized(next, prev) {
            if (next === false && prev === true) {
                this.$root.securityService.checkAuth({ requiresAuth: this.$route.meta.requiresAuth });
            }
            if (next === true) {
                this.loadProfileData();
            }
        },
        requiredToFill() {
            if (this.requiredToFill) {
                this.showProfilePopup = true;
            }
        },
    },
    mounted() {
        this.translateDetector();
    },

    async created() {
        wsSetServices({
            notify: this.onNotificationMessage,
        });
        await this.$store.dispatch('auth/registerSecurityService', this.$root.securityService);
        this.isLoading = false;
        httpRegisterSecurityService(this.$root.securityService);
        wsConnectorRegisterSecurityService(this.$root.securityService);

        this.noneHeaderRoutes.forEach((r) => {
            if (this.$route.fullPath.includes(r)) {
                this.isHeader = false;
            }
        });
        this.loadDictionary();

        // adaptive resize
        window.addEventListener('resize', this.resize);
        this.resize();

        await init(this.$root.securityService);
        setService('main', this.onNotificationMessage);
        // this.loadAuthData();
    },

    methods: {
        ...mapActions(actions),
        ...mapActions('popupsController', popupsControllerActions),

        popupHandler(e) {
            this.closeAllPopups(e);
        },
        exitImpersonation() {
            const returnUrl = `https://administrator.${window.rsvDomain}/stop/${this.userId}`;
            document.location = makeURL('identity', `Admin/StopImpersonation?returnUrl=${returnUrl}`);
        },
        translateDetector() {
            // Ссылка на элемент <html>
            const htmlElement = document.documentElement;

            // Создаем MutationObserver для отслеживания изменений атрибутов
            const observer = new MutationObserver((mutationsList) => {
                for (let mutation of mutationsList) {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'lang') {
                        // Логика при изменении атрибута lang
                        const newLang = htmlElement.getAttribute('lang');

                        if (newLang !== 'ru') {
                            this.$store.dispatch('addNotify', {
                                text: 'Для корректного отображения текста рекомендуем вам выключить переводчик.',
                                component: 'NotificationApp',
                                key: 'translate-checker',
                            });
                        } else {
                            const notifies = this.$store.state.notifyApp.notifies;

                            notifies.some((noty, index) => {
                                if (noty.key === 'translate-checker') {
                                    this.$store.dispatch('removeNotify', index);
                                    return true;
                                }
                            });
                        }
                    }
                }
            });

            // Начинаем отслеживать изменения атрибутов у тега <html>
            observer.observe(htmlElement, { attributes: true });

            this.$once('hook:beforeDestroy', () => {
                observer.disconnect();
            });
        },
    },
};
</script>

<style lang="scss">
@import 'fonts/CirceRounded.css';
@import 'fonts/Montserrat.css';
@import 'fonts/open-sans/OpenSans.css';
@import './styles/rem';
@import './styles/mrem';
@import './styles/variables';
@import './styles/main';
@import './styles/buttons';
@import '~vuejs-noty/dist/vuejs-noty.css';

body {
    letter-spacing: initial;
}

@import './static/styles/for_blind/special.min.css';

$bgColor: #edeef0;

.app-loader {
    &--overlay {
        position: absolute;
        top: 0;
        left: 0;
        background-color: $bgColor;
    }

    &-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        width: 100%;
    }
    &-message {
        font-family: 'Circe Rounded', Calibri, Candara, Segoe, 'Segoe UI', Optima, Arial, sans-serif;
        font-size: mrem(18);
        line-height: mrem(23);

        @include on-desktop {
            font-size: rem(18);
            line-height: rem(23);
        }
    }
}

#noty_layout__topRight {
    top: rem(112);
    right: rem(8);
    width: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    @media (max-width: $vm) {
        top: mrem(62);
        right: mrem(11);
    }

    .noty_bar {
        border-radius: rem(4);
        margin: rem(8) 0;
        flex: 0 0 auto;

        @media (max-width: $vm) {
            border-radius: mrem(4);
            margin: mrem(8) 0;
        }

        .noty_body {
            padding: rem(16) rem(24);
            font-family: 'Open Sans', sans-serif;
            font-style: normal;
            font-weight: 600;
            font-size: rem(14);
            line-height: rem(24);

            @media (max-width: $vm) {
                padding: mrem(12) mrem(18);
                font-size: mrem(12);
                line-height: mrem(20);
            }
        }
        &.noty_type__success {
            background: #dff8e4;
            border: 1px solid #68c87a;
            color: #00a71f;
        }
        &.noty_type__warning {
            background: #fdf6ed;
            border: 1px solid #f1ae47;
            color: #eb991d;
        }
        &.noty_type__error {
            background: #fef0f0;
            border: 1px solid #f36d6f;
            color: #f9595b;
        }
    }
    .noty_progressbar {
        display: none !important;
    }
}

.route {
    &-main {
        @include on-mobile {
            padding-top: 5rem !important;
        }
    }
}

* {
    box-sizing: border-box;
    outline: none;
    -webkit-tap-highlight-color: transparent;

    a {
        color: #333;
    }
}

html {
    font-size: 1vw;
    // font-family: "Montserrat", sans-serif;
    font-family: 'Proxima Nova', sans-serif !important;
    overflow-y: scroll;

    &.no-scroll {
        overflow: hidden;
    }
}

body {
    overflow-y: hidden;
    margin: 0;
    padding: 0;
    overflow-x: hidden;
    background: $bgColor;
    background-size: 75rem;
    color: #2a2626;

    @media (max-width: 768px) {
        background: #f7fafc;
    }

    &.modal-open {
        overflow: hidden;
        position: fixed;
    }
}

.bg-white {
    background: #fff;
}

a {
    text-decoration: none;
}

.app {
    position: relative;
    overflow-x: hidden;
    &-body {
        &_impersonation {
            margin-top: mrem(40);
            @include on-desktop {
                margin-top: rem(40);
            }
        }
    }
}

.blurred {
    filter: blur(3px);
}

.wrap {
    padding-top: mrem(56);
    width: 100rem;

    @include on-desktop {
        padding-top: rem(100);
    }
}

.isMobileApp {
    padding-top: 0;
}

.shadow {
    box-shadow: 0 rem(1) rem(4) rgba(155, 161, 166, 0.23626);
}

.col-center {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.btn {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 10rem;
    height: 2rem;
    border-radius: 0.2rem;
    font-size: 0.7rem;
    font-weight: bold;
    color: #fff;
    cursor: pointer;
}

/** BREADCRUMBS **/

.breadcrumbs {
    display: flex;
    align-items: center;
    font-size: 0.9rem;
    font-weight: 500;
    height: 5rem;
    user-select: none;

    .grey {
        color: #98a0a9;
    }

    .divider {
        margin: 0 0.5rem;
    }

    .item:not(:last-child):after {
        margin: 0 0.5rem;
        content: '/';
        color: #98a0a9;
    }
}

@include on-uxs {
    // .wrap {
    //     padding-top: 15rem;
    // }
    .isMobileApp {
        padding-top: 0;
    }
    .breadcrumbs {
        font-size: 3.5rem;

        .item:not(:last-child):after {
            margin: 0 3rem;
            content: '/';
            color: #98a0a9;
        }

        .divider {
            margin: 0 3rem;
        }
    }
}

.exit-impersonation {
    width: 100%;
    background: #db6c61;
    color: #ffffff;
    position: fixed;
    top: 0;
    left: 0;
    height: mrem(40);
    padding: mrem(7);
    font-weight: 600;
    font-size: mrem(16);
    line-height: mrem(26);
    text-align: center;
    cursor: pointer;
    z-index: 1000;
    img {
        width: mrem(18);
        height: mrem(14);
        margin-right: mrem(10);
    }
    @include on-desktop {
        height: rem(40);
        padding: rem(7);
        font-weight: 600;
        font-size: rem(16);
        line-height: rem(26);
        img {
            width: rem(18);
            height: rem(14);
            margin-right: rem(10);
        }
    }
}

/** DATA MODELS **/

/*<!--.data-model {-->*/
/*<!--    color: #98A0A9;-->*/
/*<!--    // border: solid rem(1);-->*/
/*<!--    // border-radius: .2rem;-->*/
/*<!--    // height: 2.2rem;-->*/
/*<!--    // position: relative;-->*/

/*<!--    !*.label, *!-->*/
/*<!--    .coins {-->*/
/*<!--        position: absolute;-->*/
/*<!--        top: -.4rem;-->*/
/*<!--        background-color: #fff;-->*/
/*<!--        padding: 0 .4rem;-->*/
/*<!--        font-size: .6rem;-->*/
/*<!--    }-->*/

/*<!--    .coins {-->*/
/*<!--        right: 1rem;-->*/
/*<!--        color: #EF627D;-->*/
/*<!--        font-weight: 500;-->*/
/*<!--        padding-right: 1.4rem;-->*/
/*<!--        background-image: url(./assets/images/account/coin.svg);-->*/
/*<!--        background-size: auto 100%;-->*/
/*<!--        background-repeat: no-repeat;-->*/
/*<!--        background-position: right .4rem center;-->*/
/*<!--    }-->*/

/*<!--    .label {-->*/
/*<!--        left: .4rem;-->*/
/*<!--        font-weight: 600;-->*/
/*<!--        z-index: 1;-->*/
/*<!--        pointer-events: none;-->*/
/*<!--        transition: .1s;-->*/

/*<!--        &.hold-place {-->*/
/*<!--            top: .45rem;-->*/
/*<!--            background-color: transparent;-->*/
/*<!--            font-size: .8rem;-->*/
/*<!--            font-weight: 500;-->*/
/*<!--        }-->*/
/*<!--    }-->*/
/*<!--}-->*/

/** IMAGES **/

.examples-img {
    background-image: url(./assets/images/world/examples-m.png);
}

.gains-img {
    background-image: url(./assets/images/world/gains-m.png);
}

.RMain-img {
    background-image: url(./assets/images/world/RMain-m.png);
}

.join-img {
    background-image: url(./assets/images/world/join-m.png);
}

.news-img {
    background-image: url(./assets/images/world/news-m.png);
}

.opportunities-img {
    background-image: url(./assets/images/world/opportunities-m.png);
}

.growth-img {
    background-image: url(./assets/images/world/growth-m.png);
}

.pumps-img {
    background-image: url(./assets/images/world/pumps-m.png);
}

.router-link-active .RMain-img,
.RMain-head {
    background-image: url(./assets/images/world/RMain-c.png);
}

.router-link-active .examples-img,
.examples-head {
    background-image: url(./assets/images/world/examples-c.png);
}

.router-link-active .gains-img,
.gains-head {
    background-image: url(./assets/images/world/gains-c.png);
}

.router-link-active .join-img,
.join-head {
    background-image: url(./assets/images/world/join-c.png);
}

.router-link-active .news-img,
.news-head {
    background-image: url(./assets/images/world/news-c.png);
}

.router-link-active .opportunities-img,
.opportunities-head {
    background-image: url(./assets/images/world/opportunities-c.png);
}

.router-link-active .growth-img,
.growth-head {
    background-image: url(./assets/images/world/growth-c.png);
}

.router-link-active .pumps-img,
.pumps-head {
    background-image: url(./assets/images/world/pumps-c.png);
}

@for $i from 0 through 6 {
    .mx-#{$i} {
        margin-left: $i * 0.3em;
        margin-right: $i * 0.25em;
    }
    .my-#{$i} {
        margin-top: $i * 0.25em;
        margin-bottom: $i * 0.25em;
    }
    .mt-#{$i} {
        margin-top: $i * 0.25em;
    }
    .mb-#{$i} {
        margin-bottom: $i * 0.25em;
    }
    .ml-#{$i} {
        margin-left: $i * 0.25em;
    }
    .mr-#{$i} {
        margin-right: $i * 0.25em;
    }

    .px-#{$i} {
        padding-left: $i * 0.25em;
        padding-right: $i * 0.25em;
    }
    .py-#{$i} {
        padding-top: $i * 0.25em;
        padding-bottom: $i * 0.25em;
    }
    .pt-#{$i} {
        padding-top: $i * 0.25em;
    }
    .pb-#{$i} {
        padding-bottom: $i * 0.25em;
    }
    .pl-#{$i} {
        padding-left: $i * 0.25em;
    }
    .pr-#{$i} {
        padding-right: $i * 0.25em;
    }
}

@for $i from 1 through 100 {
    .col-#{$i} {
        width: $i * 1%;
        display: inline-block;
    }
}

@include on-mobile {
    .slide-left-enter-active,
    .slide-right-enter-active {
        transition: all 0.25s ease-in-out 0.25s;
    }

    .slide-left-leave-active,
    .slide-right-leave-active {
        transition: all 0.25s ease-in-out;
    }

    .slide-left-enter,
    .slide-left-leave-to {
        transform: translateX(-100vw);
    }

    .slide-right-enter,
    .slide-right-leave-to {
        transform: translateX(100vw);
    }
}
</style>
